// Saves a LineChart.jsx <svg> as a downloadable PNG. Nothing in this
// codebase downloads a file today -- no Blob/createObjectURL/<a download>
// anywhere in ui/src -- so this establishes that pattern, deliberately
// built on native browser APIs with zero new dependencies (there's nothing
// in the SVG that needs a real rasterization library: no external image/
// font references, and every color is already an inline attribute rather
// than a CSS class -- see LineChart.jsx).
//
// Several correctness traps had to be handled explicitly, each because the
// naive version silently produces a wrong-looking image rather than an
// error:
//
// 1. LineChart's live <svg> has width="100%" -- a percentage with no
//    resolvable reference once serialized standalone into an Image(). The
//    clone's width/height are overwritten to literal pixel values before
//    serializing, and those same literal numbers (never img.naturalWidth/
//    naturalHeight) drive the canvas and drawImage() calls.
// 2. Font inheritance breaks across the document boundary: the live
//    <text> elements have no explicit font-family and visually inherit
//    the app's font via normal CSS cascade on screen, but a serialized
//    SVG loaded through a blob Image() is an independent resource with no
//    access to the host page's stylesheet -- exported text would silently
//    fall back to the browser's UA default (typically serif). Every
//    cloned <text> node gets an explicit inline font-family before
//    serializing.
// 3. The SVG paints no background of its own (transparent) -- pasted onto
//    a dark slide that reads as broken, so the canvas is explicitly
//    filled with the caller-supplied background color (the live theme's
//    background.paper, captured by the caller at export time -- this
//    module has no theme access of its own).
export async function exportChartPng({ svg, backgroundColor, captionColor, fontFamily, caption, filename }) {
  if (!svg) throw new Error('exportChartPng: no <svg> element to export')

  const scale = 2 // retina-quality output
  const viewBox = svg.getAttribute('viewBox') ?? `0 0 ${svg.clientWidth} ${svg.clientHeight}`
  const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(Number)
  const width = Math.round(vbWidth * scale)
  const height = Math.round(vbHeight * scale)
  const captionBandHeight = caption ? 18 * scale : 0

  const clone = svg.cloneNode(true)
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('width', String(width))
  clone.setAttribute('height', String(height))
  for (const textEl of clone.querySelectorAll('text')) {
    textEl.setAttribute('style', `font-family: ${fontFamily}`)
  }

  const svgString = new XMLSerializer().serializeToString(clone)
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const svgUrl = URL.createObjectURL(svgBlob)

  const img = new Image()
  img.src = svgUrl
  await img.decode()
  URL.revokeObjectURL(svgUrl)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height + captionBandHeight
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, width, height)

  if (caption) {
    ctx.font = `${12 * scale}px ${fontFamily}`
    ctx.fillStyle = captionColor
    ctx.textAlign = 'center'
    ctx.fillText(caption, canvas.width / 2, height + captionBandHeight / 2 + 4)
  }

  const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
  const pngUrl = URL.createObjectURL(pngBlob)
  const a = document.createElement('a')
  a.href = pngUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(pngUrl)
}
